// Components
import { ConfigContext, HeaderV2, PillTabsV2, StandingsTable, PlayoffKnockoutNew } from '@velinsi/smp-widgets-ui';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';

// Utils
import { useStandings, useStandingsCompetition } from '../../../../../packages/hooks/standings';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import usePlayoff from '../../../../../packages/hooks/usePlayoffs';
import { defaultHandleEventClick, defaultHandleTournamentClick, defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils';

// Types/Constants
import { BaseWidgetProps, SportTypes } from '../../../../../packages/types';
import { IStandingsConfig } from '../../../../../packages/types/standings';
import { TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

export type StandingsWidgetProps = BaseWidgetProps<IStandingsConfig>;

export const BasketballStandingsWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: StandingsWidgetProps) => {
  const sport = SportTypes.BASKETBALL;
  const { ref } = useRootNodeRef();

  const {
    isLoading: competitionLoading,
    competition,
    pillTabOptions,
    seasonOptions,
    season,
    setSeason,
    stage,
    stages,
    setStage,
  } = useStandingsCompetition({
    ...config,
    sport,
    sdkOptions,
  });

  const {
    isLoading: standingsLoading,
    standings,
    groupTypeOptions,
    groupType,
    setGroupType,
    handleClick,
  } = useStandings({
    ...config,
    dataWidgetCuid: cuid,
    stage_id: stage,
    sport,
    sdkOptions,
    subscribers,
  });

  const {
    playoffStage,
    isLoading: playoffsLoading,
    isPlayoffStageSelected,
  } = usePlayoff({
    availableStages: stages,
    stageId: stage,
    sdkOptions,
    dataEntityLinks: config?.dataEntityLinks || undefined,
    sport,
  });

  const handleSeasonChange = (value?: string) => {
    setSeason(value);
    setStage('');
  };

  const handlePillTabChange = (value: string) => {
    setStage(value);

    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  const handleGroupTypeTabChange = (value: string) => {
    setGroupType(value);
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  const handleTournamentClick = () => defaultHandleTournamentClick(handleClick, competition.id, competition.title);

  const handleEventClick = (event: string) => defaultHandleEventClick(handleClick, event);

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={competitionLoading || standingsLoading || playoffsLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-primaryBackgroundColor)'
      >
        <HeaderV2
          {...competition}
          selectedSeason={season}
          selectedPillTab={stage ?? ''}
          displayPillTabs={config?.dataOptionsDisplay}
          displaySeasonDropdown={config?.dataHeaderDisplay}
          pillTabOptions={pillTabOptions}
          seasonOptions={seasonOptions}
          handleHeaderClick={handleTournamentClick}
          handlePillTabChange={handlePillTabChange}
          handleSeasonChange={handleSeasonChange}
        />
        {config?.dataOptionsDisplay && (
          <div className='px-4 pb-3 pt-0 bg-rowBackgroundColor groupTypes-pill-tabs-container'>
            <PillTabsV2 selectedPillTab={groupType} pillTabOptions={groupTypeOptions} handlePillTabChange={handleGroupTypeTabChange} />
          </div>
        )}
        {standings && <StandingsTable standings={standings} handleClick={handleClick} sport={SportTypes.BASKETBALL} />}
        {isPlayoffStageSelected && <PlayoffKnockoutNew playoff={playoffStage} handleEventClick={handleEventClick} />}
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
